import { MailOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Empty,
  Input,
  Skeleton,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CandidateMessage } from '../models/candidate-message.model';
import { CandidateMessageService } from '../services/candidate-message.service';
import './CandidateMessagesPanel.css';

interface Props {
  applicationId: string;
  candidateName: string;
  jobTitle: string;
  emailAllowed: boolean;
  applicationOpen?: boolean;
  focusedMessageId?: string;
}

const formatMessageTime = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const CandidateMessagesPanel: React.FC<Props> = ({
  applicationId,
  candidateName,
  jobTitle,
  emailAllowed,
  applicationOpen = true,
  focusedMessageId,
}) => {
  const [messages, setMessages] = useState<CandidateMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const sendingAllowed = emailAllowed && applicationOpen;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const thread = await CandidateMessageService.list(applicationId);
      setMessages(thread.messages || []);
    } catch {
      message.error('Could not load the candidate conversation.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    if (focusedMessageId) {
      document
        .getElementById(`candidate-message-${focusedMessageId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [focusedMessageId, loading, messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending || !sendingAllowed) return;
    setSending(true);
    try {
      const sent = await CandidateMessageService.send(applicationId, body);
      setMessages((current) => [...current, sent]);
      setDraft('');
      message.success(`Message emailed to ${candidateName}.`);
    } catch {
      message.error('Could not send the message.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="candidate-messages"
      aria-labelledby="candidate-messages-heading"
    >
      <header className="candidate-messages__header">
        <div className="candidate-messages__identity">
          <span aria-hidden="true">
            <MessageOutlined />
          </span>
          <div>
            <Typography.Title level={4} id="candidate-messages-heading">
              Messages
            </Typography.Title>
            <Typography.Text type="secondary">
              Chat with {candidateName} about {jobTitle}. Messages never change
              the hiring stage.
            </Typography.Text>
          </div>
        </div>
        <Tag icon={<MailOutlined />} color={emailAllowed ? 'blue' : 'default'}>
          {emailAllowed ? 'Email connected' : 'Email unavailable'}
        </Tag>
      </header>

      {!emailAllowed ? (
        <Alert
          type="warning"
          showIcon
          message="This candidate cannot receive email messages"
          description="Add an eligible email address or enable email notifications before starting the conversation."
        />
      ) : !applicationOpen ? (
        <Alert
          type="warning"
          showIcon
          message="Messaging is unavailable for this application"
          description="Closed or deleted applications cannot receive new messages. The existing conversation remains available."
        />
      ) : null}

      <div className="candidate-messages__conversation" aria-live="polite">
        {loading ? (
          <div role="status">
            <Skeleton active avatar paragraph={{ rows: 4 }} />
            <span className="sr-only">Loading conversation</span>
          </div>
        ) : messages.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No messages yet. Write the first message below."
          />
        ) : (
          messages.map((item) => (
            <article
              id={`candidate-message-${item.id}`}
              key={item.id}
              className={`candidate-message candidate-message--${item.direction.toLowerCase()}${focusedMessageId === item.id ? ' candidate-message--focused' : ''}`}
            >
              <div>
                <strong>{item.senderName}</strong>
                <time dateTime={item.createdAt}>
                  {formatMessageTime(item.createdAt)}
                </time>
              </div>
              <p>{item.body}</p>
              {item.direction === 'RECRUITER' && item.emailQueued ? (
                <small>
                  <MailOutlined /> Email queued
                </small>
              ) : null}
            </article>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="candidate-messages__composer">
        <Input.TextArea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPressEnter={(event) => {
            if (event.shiftKey) return;
            event.preventDefault();
            void send();
          }}
          autoSize={{ minRows: 3, maxRows: 7 }}
          maxLength={5000}
          showCount
          disabled={!sendingAllowed || sending}
          placeholder={`Message ${candidateName}…`}
          aria-label={`Message ${candidateName}`}
        />
        <div className="candidate-messages__composer-footer">
          <span>Enter to send · Shift+Enter for a new line</span>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={!sendingAllowed || !draft.trim()}
            onClick={() => void send()}
          >
            Send
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CandidateMessagesPanel;
